import { compact } from "lodash";
import moment from "moment";
import { useContext, useEffect, useMemo, useState } from "react";
import { BasicUser } from "types/topicflow";

import AskAI from "@apps/ask-ai/ask-ai";
import AssessmentGoalSidebar from "@apps/assessments/components/assessment/assessment-goal-sidebar";
import Feedbacks from "@apps/dashboard-new/components/feedbacks";
import Recognitions from "@apps/dashboard-new/components/recognitions";
import PerformanceAssessmentReportContext from "@apps/reporting/context/performance-assessment-report-context";
import WorkHistorySidebar from "@apps/work-history/components/work-history-sidebar";
import { currentOrganizationVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Layout from "@components/layout/layout";
import Sidebar from "@components/sidebar/sidebar";
import {
  ToggleButtonGroup,
  ToggleButtonGroupTheme,
} from "@components/toggle-button-group/toggle-button-group";

import EmployeeDrawerReviews from "./employee-drawer-reviews";
import EmployeeDrawerRoles from "./employee-drawer-roles";

enum EmployeeDrawerSidebarTab {
  Reviews = "Reviews",
  Roles = "Roles",
  Items = "Items",
  WorkHistory = "Work History",
  AskAI = "Ask AI",
}

const EmployeeDrawerSidebar = ({
  target,
  onClose,
}: {
  target: BasicUser;
  onClose: () => void;
}) => {
  const organization = currentOrganizationVar();
  const { complianceProgram, complianceProgramId } = useContext(
    PerformanceAssessmentReportContext,
  );

  const [expandToUrl, setExpandToUrl] = useState<string | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<
    EmployeeDrawerSidebarTab | undefined
  >(EmployeeDrawerSidebarTab.Reviews);
  const [chatSessionId, setChatSessionId] = useState<null | number>(null);
  const [startedChatSession, setStartedChatSession] = useState(false);

  useEffect(() => {
    if (activeTab !== EmployeeDrawerSidebarTab.Reviews) {
      setExpandToUrl(undefined);
    }
  }, [activeTab]);

  const hasNoContentInArtifactTab =
    !organization.featureFlags.goals &&
    !organization.featureFlags.recognitions &&
    !organization.featureFlags.feedbacks;

  const tabs = useMemo(() => {
    return compact([
      {
        title: EmployeeDrawerSidebarTab.Reviews,
        value: EmployeeDrawerSidebarTab.Reviews,
      },
      {
        title: EmployeeDrawerSidebarTab.Roles,
        value: EmployeeDrawerSidebarTab.Roles,
      },
      !hasNoContentInArtifactTab && {
        title: EmployeeDrawerSidebarTab.Items,
        value: EmployeeDrawerSidebarTab.Items,
      },
      organization.featureFlags.workHistory && {
        title: EmployeeDrawerSidebarTab.WorkHistory,
        value: EmployeeDrawerSidebarTab.WorkHistory,
      },
      organization.featureFlags.askAi && {
        title: EmployeeDrawerSidebarTab.AskAI,
        value: EmployeeDrawerSidebarTab.AskAI,
      },
    ]);
  }, [hasNoContentInArtifactTab, organization.featureFlags]);

  return (
    <Sidebar
      onClose={onClose}
      fullHeight={activeTab === EmployeeDrawerSidebarTab.AskAI}
      expandToUrl={expandToUrl}
    >
      <div className="px-6 py-4 top-0 sticky bg-white z-sticky border-b -mb-px">
        <div className="flex items-center gap-3 mb-4">
          <Avatar user={target} size="6" />
          <div className="tf-header-xl">{target.name}</div>
        </div>
        <div className="flex items-center">
          <ToggleButtonGroup
            theme={ToggleButtonGroupTheme.sidebar}
            buttons={tabs.map((tab) => ({
              ...tab,
              active: tab.value === activeTab,
            }))}
            onClick={(tab) => setActiveTab(tab.value)}
          />
        </div>
      </div>

      {activeTab === EmployeeDrawerSidebarTab.Reviews ? (
        <EmployeeDrawerReviews
          target={target}
          complianceProgramId={complianceProgramId}
          onReviewPackageSelected={(deliveryId) =>
            setExpandToUrl(`/assessments/assessment/delivery/${deliveryId}`)
          }
        />
      ) : activeTab === EmployeeDrawerSidebarTab.Roles ? (
        <EmployeeDrawerRoles target={target} />
      ) : activeTab === EmployeeDrawerSidebarTab.AskAI ? (
        <AskAI
          isInSidebar={true}
          chatSessionId={chatSessionId}
          onChangeChatSessionId={setChatSessionId}
          isFullScreen={!!chatSessionId || startedChatSession}
          className="p-6 h-full"
          totalYPaddingAroundContainer={150}
          onStartChatSession={() => {
            setStartedChatSession(true);
          }}
        />
      ) : activeTab === EmployeeDrawerSidebarTab.WorkHistory ? (
        <div className="p-6">
          <WorkHistorySidebar
            userId={target.id}
            startDate={
              complianceProgram?.periodStartDate ||
              moment().subtract(7, "days").format("YYYY-MM-DD")
            }
            endDate={
              complianceProgram?.periodEndDate || moment().format("YYYY-MM-DD")
            }
            forceDateFilter={true}
          />
        </div>
      ) : activeTab === EmployeeDrawerSidebarTab.Items ? (
        <>
          {hasNoContentInArtifactTab && (
            <Layout.SidebarSection>
              <Layout.SidebarSubSectionEmpty>
                No related data.
              </Layout.SidebarSubSectionEmpty>
            </Layout.SidebarSection>
          )}

          {organization.featureFlags.goals && target && (
            <AssessmentGoalSidebar
              selectedUser={target}
              goalDueBetweenDates={
                complianceProgram?.periodStartDate &&
                complianceProgram?.periodEndDate
                  ? [
                      complianceProgram.periodStartDate,
                      complianceProgram.periodEndDate,
                    ]
                  : undefined
              }
            />
          )}

          {organization.featureFlags.recognitions && target && (
            <Recognitions
              selectedUser={target}
              createdBetweenDates={
                complianceProgram?.periodStartDate &&
                complianceProgram?.periodEndDate
                  ? [
                      complianceProgram.periodStartDate,
                      complianceProgram.periodEndDate,
                    ]
                  : undefined
              }
            />
          )}
          {organization.featureFlags.feedbacks && target && (
            <Feedbacks
              selectedUser={target}
              createdBetweenDates={
                complianceProgram?.periodStartDate &&
                complianceProgram?.periodEndDate
                  ? [
                      complianceProgram.periodStartDate,
                      complianceProgram.periodEndDate,
                    ]
                  : undefined
              }
            />
          )}
        </>
      ) : null}
    </Sidebar>
  );
};
export default EmployeeDrawerSidebar;
